import React, { useState } from 'react';
import * as Yup from 'yup';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { Link, useNavigate } from "react-router-dom";
import { Bounce, ToastContainer, toast } from 'react-toastify';
import { Form, Formik, Field, FormikHelpers, FormikTouched, FormikErrors } from "formik";

import './Form.css';
import { sendDataToApi } from './FormUtils';
import FormTitle from "../../molecules/form-title/FormTitle";
import OauthLoginBtn from '../../molecules/oauth-login/OauthLoginBtn';


type FormValues = {
    email: string;
    password: string;
    confirm_password: string;
};

const RegisterForm = () => {
    const navigate = useNavigate();
    
    
    const [touchedFields, setTouchedFields] = useState<FormikTouched<FormValues>>({});

    const validationSchema = Yup.object({
        email: Yup.string().email().required('Email field is required'),
        password: Yup.string()
            .min(8, 'Password should contain at least 8 characters')
            .required('Password field is required'),
        confirm_password: Yup.string()
            .oneOf([Yup.ref('password')], 'Passwords must match')
            .required('Confirm Password field is required'),
    });

    return (
        <Formik
            initialValues={{
                email: '',
                password: '',
                confirm_password: ''
            }}
            validationSchema={validationSchema}
            onSubmit={(values: FormValues, { setTouched }: FormikHelpers<FormValues>) => {
                const registrationUrl = import.meta.env.VITE_REGISTRATION_URL as string;
                sendDataToApi(registrationUrl, '', values, navigate);
            }}
            validate={(values: FormValues) => {
                try {
                    validationSchema.validateSync(values, { abortEarly: false });
                } catch (error) {
                    if (error instanceof Yup.ValidationError) {
                        const fieldErrors: FormikErrors<FormValues> = {};
                        error.inner.forEach((err) => {
                            fieldErrors[err.path as keyof FormValues] = err.message;
                        });

                        
                        const errorKeys = Object.keys(touchedFields) as Array<keyof FormValues>;
                        const filteredErrors = errorKeys.filter((field) => touchedFields[field]);

                        if (filteredErrors.length > 0) {
                            filteredErrors.forEach((field) => {
                                toast.error(fieldErrors[field], {
                                    position: "top-right",
                                    autoClose: 5000,
                                    hideProgressBar: false,
                                    closeOnClick: true,
                                    pauseOnHover: true,
                                    draggable: true,
                                    progress: undefined,
                                    theme: "dark",
                                    transition: Bounce,
                                });
                            });
                        }
                    }
                }
            }}
        >
            {(formik) => (
                <Form className="formContainer" onSubmit={formik.handleSubmit}>
                    <ToastContainer />
                    <FormTitle title="Sign Up" text="Create your account today" />
                    <OauthLoginBtn microsoft='Sign Up With Microsoft' google='Sign Up With Google' />
                    
                    <div className="formField" id="sponsorRegistration">
                        <div className="hrContainer">
                            <hr className="leftHr" />
                            <small className="hrText">or use email</small>
                            <hr className="rightHr" />
                        </div>

                        
                        <div className="fieldAndIcon">
                            <div className="icon">
                                <PersonIcon />
                            </div>
                            <Field
                                autoComplete="off"
                                className="formInput"
                                id="email"
                                name="email"
                                type="email"
                                placeholder="Email"
                                onChange={formik.handleChange}
                                onBlur={() => setTouchedFields({ ...touchedFields, email: true })}
                                value={formik.values.email}
                            />
                        </div>

                        
                        <div className="fieldAndIcon">
                            <div className="icon">
                                <LockIcon />
                            </div>
                            <Field
                                autoComplete="off"
                                className="formInput"
                                id="password"
                                name="password"
                                type="password"
                                placeholder="Password"
                                onChange={formik.handleChange}
                                onBlur={() => setTouchedFields({ ...touchedFields, password: true })}
                                value={formik.values.password}
                            />
                        </div>

                        
                        <div className="fieldAndIcon">
                            <div className="icon">
                                <LockIcon />
                            </div>
                            <Field
                                autoComplete="off"
                                className="formInput"
                                id="confirm_password"
                                name="confirm_password"
                                type="password"
                                placeholder="Confirm Password"
                                onChange={formik.handleChange}
                                onBlur={() => setTouchedFields({ ...touchedFields, confirm_password: true })}
                                value={formik.values.confirm_password}
                            />
                        </div>

                        <div className="termsAgreement">
                            <input type="checkbox" name="privacyPolicy" id="privacyPolicy" />
                            <label htmlFor="privacyPolicy">
                                I accept the <span className="privacyPolicyBold">terms</span> and <span className="privacyPolicyBold">privacy policy</span>
                            </label>
                        </div>
                        <input className="submitBtn" type="submit" value="Register" />
                        <small className="formText">
                            Already have an account? <Link to="/login"><span>Login</span></Link>
                        </small>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default RegisterForm;
